// Hero canvas — variant 2: animated particle wave grid.
// Built with React.createElement for R3F intrinsics so the dev-time
// visual-edits babel plugin does not inject incompatible attributes.
import React, { Suspense, useMemo, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { useTheme } from "../../contexts/ThemeContext";

const h = React.createElement;

const COLS = 80;
const ROWS = 48;
const SPACING = 0.35;

function WaveField({ mouse, isDark }) {
  const ref = useRef();

  const { positions, count } = useMemo(() => {
    const pts = [];
    for (let y = 0; y < ROWS; y++) {
      for (let x = 0; x < COLS; x++) {
        pts.push(
          (x - COLS / 2) * SPACING,
          0,
          (y - ROWS / 2) * SPACING,
        );
      }
    }
    return { positions: new Float32Array(pts), count: pts.length / 3 };
  }, []);

  useFrame((state) => {
    const t = state.clock.getElapsedTime();
    if (!ref.current) return;
    const arr = ref.current.geometry.attributes.position.array;
    const mx = mouse.current.x * 4;
    const my = mouse.current.y * 4;
    for (let i = 0; i < count; i++) {
      const ix = i * 3;
      const px = arr[ix];
      const pz = arr[ix + 2];
      const dx = px - mx;
      const dz = pz + my;
      const dist = Math.sqrt(dx * dx + dz * dz);
      const wave =
        Math.sin(px * 0.5 + t * 0.9) * 0.35 +
        Math.cos(pz * 0.5 + t * 0.7) * 0.35 +
        Math.cos(dist * 0.7 - t * 1.6) * 0.6;
      arr[ix + 1] = wave;
    }
    ref.current.geometry.attributes.position.needsUpdate = true;
    ref.current.rotation.y = t * 0.04;
  });

  return h(
    "points",
    { ref, rotation: [-Math.PI / 3.2, 0, 0], position: [0, -0.6, 0] },
    h("bufferGeometry", null,
      h("bufferAttribute", {
        attach: "attributes-position",
        array: positions,
        count,
        itemSize: 3,
      }),
    ),
    h("pointsMaterial", {
      size: isDark ? 0.045 : 0.035,
      color: isDark ? "#ffffff" : "#0a0a0a",
      sizeAttenuation: true,
      transparent: true,
      opacity: isDark ? 0.85 : 0.7,
      depthWrite: false,
      blending: isDark ? THREE.AdditiveBlending : THREE.NormalBlending,
    }),
  );
}

function Scene({ mouse, isDark }) {
  const bg = isDark ? "#050505" : "#fafafa";
  return h(
    React.Fragment,
    null,
    h("color", { attach: "background", args: [bg] }),
    h("fog", { attach: "fog", args: [bg, 7, 18] }),
    h(Suspense, { fallback: null }, h(WaveField, { mouse, isDark })),
  );
}

export default function HeroCanvasParticles() {
  const mouse = useRef({ x: 0, y: 0 });
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const onPointer = (e) => {
    const x = (e.clientX / window.innerWidth) * 2 - 1;
    const y = -(e.clientY / window.innerHeight) * 2 + 1;
    mouse.current.x = x;
    mouse.current.y = y;
  };

  return (
    <div
      className="absolute inset-0 -z-0"
      onPointerMove={onPointer}
      data-testid="hero-canvas-particles"
      aria-hidden="true"
    >
      <Canvas
        key={theme}
        dpr={[1, 1.6]}
        camera={{ position: [0, 2.2, 8.5], fov: 50 }}
        gl={{ antialias: true, alpha: true, powerPreference: "high-performance" }}
      >
        <Scene mouse={mouse} isDark={isDark} />
      </Canvas>
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background: isDark
            ? "radial-gradient(ellipse at center, transparent 30%, rgba(5,5,5,0.9) 90%)"
            : "radial-gradient(ellipse at center, transparent 30%, rgba(250,250,250,0.9) 90%)",
        }}
      />
    </div>
  );
}
